import {
  isEnterprisePlanPrefix,
  isFreeTrialPhonePlan,
} from "@app/lib/plans/plan_codes";
import { useAppRouter } from "@app/lib/platform";
import type { SubscriptionType } from "@app/types/plan";
import { isCreditPricedPlan } from "@app/types/plan";
import { ONE_DAY_MS } from "@app/types/shared/utils/date_utils";
import { Button, cn } from "@dust-tt/sparkle";
import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { useMemo, useRef } from "react";

const SUBSCRIPTION_BANNER_DISPLAY_THRESHOLD_DAYS = 30;
const THRESHOLD_MS = SUBSCRIPTION_BANNER_DISPLAY_THRESHOLD_DAYS * ONE_DAY_MS;

function isSubscriptionManagementRoute(path: string) {
  return (
    /^\/w\/[^/]+\/billing$/.test(path) ||
    /^\/w\/[^/]+\/subscription$/.test(path)
  );
}

interface SubscriptionEndBannerProps {
  isAdmin: boolean;
  owner: { sId: string };
  subscription: SubscriptionType;
}

export function SubscriptionEndBanner({
  isAdmin,
  owner,
  subscription,
}: SubscriptionEndBannerProps) {
  const { t } = useLingui();
  const router = useAppRouter();
  const endDate = subscription.endDate;
  const isTrial = isFreeTrialPhonePlan(subscription.plan.code);
  const isEnterprise = isEnterprisePlanPrefix(subscription.plan.code);
  const currentPath = router.pathname || router.asPath.split("?")[0];
  const ctaHref = isCreditPricedPlan(subscription.plan)
    ? `/w/${owner.sId}/billing`
    : `/w/${owner.sId}/subscription`;

  // Capture initial timestamp in a ref to avoid re-computation on re-renders.
  // This is intentionally not reactive - the banner state is stable for the session.
  const nowRef = useRef(Date.now());

  const bannerState = useMemo(() => {
    if (!endDate) {
      return null;
    }

    const now = nowRef.current;

    if (endDate > now + THRESHOLD_MS) {
      return null;
    }

    const hasEnded = endDate < now;
    const daysRemaining = Math.max(0, Math.ceil((endDate - now) / ONE_DAY_MS));

    return { hasEnded, daysRemaining };
  }, [endDate]);

  if (isSubscriptionManagementRoute(currentPath)) {
    return null;
  }

  if (!bannerState) {
    return null;
  }

  const { hasEnded, daysRemaining } = bannerState;

  let title: string;
  let description: string;

  if (isTrial) {
    title = hasEnded
      ? t`Heads up, your trial has ended`
      : t`${plural(daysRemaining, {
          one: "Heads up, your trial ends in # day",
          other: "Heads up, your trial ends in # days",
        })}`;
    description = t`When your trial wraps up, your connections and team member access will be removed. Subscribe now to keep everything.`;
  } else if (isEnterprise) {
    title = hasEnded
      ? t`Your current subscription period has ended`
      : t`${plural(daysRemaining, {
          one: "Your current subscription period ends in # day",
          other: "Your current subscription period ends in # days",
        })}`;
    description = t`Please reach out to your account manager to ensure continuity.`;
  } else {
    title = hasEnded
      ? t`Heads up, your subscription has ended`
      : t`${plural(daysRemaining, {
          one: "Heads up, your subscription ends in # day",
          other: "Heads up, your subscription ends in # days",
        })}`;
    description = t`When your subscription wraps up, your connections and team member access will be removed. Subscribe now to keep everything.`;
  }

  return (
    <div
      className={cn(
        "flex items-center justify-between gap-4 border-b px-4 py-1",
        "border-sky-100 bg-sky-50"
      )}
    >
      <div className="text-sm flex gap-2">
        <p className={cn("font-semibold", "text-sky-900")}>{title}</p>
        <p className="text-sky-800 hidden md:inline-block">{description}</p>
      </div>
      {isAdmin && !isEnterprise && (
        <Button
          href={ctaHref}
          label={isTrial ? t`Subscribe to Dust` : t`Resume subscription`}
          className="hover:opacity-90 hover:bg-transparent"
          variant="ghost-secondary"
          size="sm"
        />
      )}
    </div>
  );
}
